/**
 * Auth route — renders the Amplify Authenticator at /login.
 *
 * D-01 (CONTEXT.md): Use the embedded @aws-amplify/ui-react <Authenticator>
 * rendered inside the React app (not Cognito Hosted UI redirect).
 *
 * UI-SPEC Screen 1:
 * - Vertically and horizontally centered on viewport
 * - Max-width: 400px, Slate-50 background (dominant 60%)
 * - Authenticator component in white card (secondary surface)
 * - ThemeProvider overrides --amplify-colors-brand-primary-* to Blue-500 (#3b82f6)
 *
 * On authenticated state: redirects to the app root (/) — the calendar.
 * NOTE: Social SSO's OAuth callback comes back to /login (every entry in
 * lib/amplify.ts's redirect list is a /login URL), so it lands on THIS route.
 * Amplify's OAuth listener completes the code exchange, then `beforeLoad`'s
 * ensureValidSession() sees the fresh session and redirects to /.
 */
import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Authenticator, ThemeProvider } from '@aws-amplify/ui-react';
import { ensureValidSession } from '@/lib/auth';
import { BrandLogo } from '@/components/BrandLogo';
import { isRunningInNativeShell } from '@/lib/pwa';
import {
  NATIVE_GOOGLE_AUTH_ERROR_EVENT,
  prepareNativeGoogleAuthUrl,
} from '@/lib/nativeGoogleAuth';
import '@aws-amplify/ui-react/styles.css';

// Fixed for the lifetime of the process — the shell can't change at runtime.
const isNative = isRunningInNativeShell();

/**
 * Google sign-in is HIDDEN FROM THE UI — deliberately, and only from the UI.
 *
 * App Store rejection 2026-08-13 (submission d86a49f2-846b-44f2-84ab-68fa0df71b9a),
 * Guideline 4.8: an app that offers a third-party login must also offer a
 * privacy-equivalent one (Sign in with Apple). Rather than ship Sign in with Apple
 * under time pressure, the owner chose to hide Google on BOTH web and native so the
 * two platforms stay identical and no reviewer sees a lone third-party button.
 *
 * NOTHING behind the button was removed: the Cognito Google IdP, the app client's
 * supportedIdentityProviders, the callback URLs, the PreSignUp linking lambda, the
 * native URL-scheme registrations, lib/nativeGoogleAuth.ts and its deep-link
 * listener are all still wired. The <NativeGoogleSignIn> component below is still
 * here too. Re-enabling is this one line — flip to true, rebuild web, rebuild the
 * shells. Do not "clean up" the now-unrendered code; that is the whole point of it.
 *
 * Explicitly typed `boolean` (not inferred `false`) so TypeScript keeps both branches
 * of the ternaries below live — the file's tsconfig sets noUnusedLocals.
 */
const GOOGLE_SSO_UI_ENABLED: boolean = false;

// UI-SPEC: ThemeProvider overrides primary color to Blue-500 (#3b82f6) to match
// the project palette's accent color.
const amplifyTheme = {
  name: 'broke-or-not-theme',
  tokens: {
    colors: {
      brand: {
        primary: {
          10: { value: '#eff6ff' },   // blue-50
          20: { value: '#dbeafe' },   // blue-100
          40: { value: '#93c5fd' },   // blue-300
          60: { value: '#3b82f6' },   // blue-500 (primary accent)
          80: { value: '#2563eb' },   // blue-600 (hover)
          90: { value: '#1d4ed8' },   // blue-700
          100: { value: '#1e40af' },  // blue-800
        },
      },
    },
  },
};

// Display-only relabel: show "Email" instead of "Username" on the Sign In and
// Create Account forms. This overrides only the visible label + placeholder — the
// Amplify field key stays `username`, so the value is still submitted to Cognito as
// the username exactly as before. No change to auth wiring.
const formFields = {
  signIn: {
    username: { label: 'Email', placeholder: 'Enter your Email' },
  },
  signUp: {
    username: { label: 'Email', placeholder: 'Enter your Email' },
  },
};

/**
 * Native-only Google button, injected as the Authenticator’s SignIn/SignUp
 * header so it sits where Amplify's own social button sits on the web.
 *
 * This is a real <a target="_blank">, not a button with an onClick, on purpose:
 * WKWebView only hands a navigation to the system browser when it comes straight
 * off a user gesture, and building the authorize URL needs an await
 * (crypto.subtle.digest for the PKCE challenge) that would break that chain. So
 * the URL — and the PKCE verifier it commits to — is prepared on mount and the
 * tap itself is a plain synchronous navigation.
 *
 * Completion happens elsewhere: lib/nativeGoogleAuth.ts's appUrlOpen listener,
 * registered at boot in main.tsx. Failures there arrive as a window event, since
 * they can land while this component is mounted but idle.
 */
function NativeGoogleSignIn({ label }: { label: string }) {
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Mints a fresh PKCE pair and points the link at it.
  const prepare = useCallback(async () => {
    setAuthUrl(null);
    try {
      setAuthUrl(await prepareNativeGoogleAuthUrl());
    } catch {
      setError('Could not start Google sign-in on this device.');
    }
  }, []);

  useEffect(() => {
    void prepare();
  }, [prepare]);

  useEffect(() => {
    const onError = (event: Event) => {
      setError((event as CustomEvent<string>).detail);
      // The deep-link handler CONSUMES the pending PKCE record (it is single-use,
      // which is what makes a replayed code fail closed). That leaves the link
      // above pointing at a state with no verifier behind it, so a retry — the
      // very next thing a user does after "you cancelled" or a network blip —
      // would fail with "session expired" instead of starting a new sign-in.
      // Re-mint so the button is immediately usable again.
      void prepare();
    };
    window.addEventListener(NATIVE_GOOGLE_AUTH_ERROR_EVENT, onError);
    return () => window.removeEventListener(NATIVE_GOOGLE_AUTH_ERROR_EVENT, onError);
  }, [prepare]);

  return (
    /* Rendered into the Authenticator's SignIn/SignUp Header slot, so it sits inside the
       card above the email/password fields — the same place Amplify puts its own social
       button on the web.

       Styling deliberately reuses AMPLIFY'S OWN class names rather than Tailwind, so the
       native button is painted by the exact same CSS as the web one instead of a hand-tuned
       approximation that drifts whenever Amplify's theme changes. Verified against the web
       DOM: button = `amplify-button amplify-field-group__control federated-sign-in-button`
       inside `amplify-flex federated-sign-in-container`, and the divider is an <hr> with
       `data-label`. That gives matching height (42px), font weight (700), border, radius,
       icon gap and the lowercase "or".

       The outer padding exists because the header slot sits OUTSIDE Amplify's <form>, so it
       doesn't inherit the form's padding — without it the button would run wider than the
       fields below. Measured off the web DOM: form padding is 12px 32px 32px. */
    <div style={{ padding: '12px 32px 0' }}>
      <div
        className="amplify-flex federated-sign-in-container"
        /* Geometry is set inline rather than relied upon from Amplify's classes: whichever
           rule gives the container `column` on the web didn't match here, and `amplify-flex`
           alone defaults to `row`, which put the divider beside the button and wrapped the
           label onto two lines. Values measured from the web DOM. */
        style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '0 0 16px' }}
      >
        <a
          href={authUrl ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          aria-disabled={!authUrl}
          onClick={(event) => {
            if (!authUrl) event.preventDefault();
            else setError(null);
          }}
          className="amplify-button amplify-field-group__control federated-sign-in-button"
          /* Amplify applies these to a <button>; an <a> needs them spelled out or the
             icon/label gap collapses ("GContinue with Google"). */
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            width: '100%',
            textDecoration: 'none',
            ...(authUrl ? null : { opacity: 0.5, pointerEvents: 'none' as const }),
          }}
        >
          <GoogleMark style={{ width: 16, height: 16, flexShrink: 0 }} />
          {label}
        </a>

        {error && (
          <p role="alert" className="text-center text-xs text-red-600">
            {error}
          </p>
        )}

        {/* Same element Amplify renders beneath its own social button on the web. */}
        <hr
          className="amplify-divider amplify-divider--horizontal amplify-divider--small"
          data-label="or"
        />
      </div>
    </div>
  );
}

/**
 * Native-only Authenticator slot overrides: the browser-handoff Google button is injected
 * as the header of both the Sign In and Create Account tabs, matching where Amplify puts
 * its own social button on the web. Defined once at module scope so the component identity
 * is stable across renders (an inline object would remount the button every render and
 * restart its PKCE preparation).
 */
const nativeAuthenticatorComponents = {
  // Labels copied verbatim from what Amplify renders on the web (read off the live DOM),
  // so the two platforms read identically per tab. Both strings are on Google's approved
  // button-text list, so matching Amplify here costs no branding compliance.
  SignIn: { Header: () => <NativeGoogleSignIn label="Sign In with Google" /> },
  SignUp: { Header: () => <NativeGoogleSignIn label="Sign Up with Google" /> },
};

/** Google's four-colour "G". Inline so the button works offline and adds no request. */
function GoogleMark({ className, style }: { className?: string; style?: CSSProperties }) {
  return (
    <svg className={className} style={style} viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

function AuthPage() {
  const navigate = useNavigate();

  return (
    <ThemeProvider theme={amplifyTheme}>
      {/* UI-SPEC Screen 1: Slate-50 background, centered layout, max-width 400px.
          <main> gives the page a single top-level landmark (a11y: landmark-one-main). */}
      <main className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        {/* Column shrinks to the Authenticator's actual width so the centered brand header
            lines up exactly over the (wider) login card instead of over a 400px box. */}
        <div className="flex flex-col items-center">
          {/* Brand header — matches the app's top-left brand (logo + wordmark) */}
          <div className="mb-6 flex flex-col items-center text-center">
            <div className="mb-3 flex items-center gap-2">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500 text-white">
                <BrandLogo className="h-7 w-7" />
              </span>
              <span className="text-2xl font-semibold tracking-tight text-slate-900">Broke or Not</span>
            </div>
            <p className="text-sm text-slate-500">See whether you can afford what's ahead.</p>
          </div>

          {/* Google sign-in is currently switched OFF in the UI — see
              GOOGLE_SSO_UI_ENABLED above for the reason. This comment describes the
              mechanism that runs when it is switched back on.

              On the web, Amplify's built-in social button calls signInWithRedirect,
              which navigates the CURRENT window to the Cognito Hosted UI — fine in a
              real browser, but Google rejects that same navigation inside a WebView
              (disallowed_useragent). So the native shells instead hide Amplify's
              button and substitute <NativeGoogleSignIn>, which hands the OAuth leg to
              the system browser. Facebook stays hidden; its IdP is still wired, so
              bringing it back is just adding 'facebook' to the socialProviders array.

              The native button goes in the SignIn/SignUp Header slots rather than after
              the Authenticator, so on native it lands inside the card above the fields —
              the same position Amplify renders its own social button on the web. Both
              platforms therefore look the same. Both tabs get it because Amplify shows
              its social buttons on Sign In and Create Account alike. */}
          <Authenticator
            socialProviders={GOOGLE_SSO_UI_ENABLED && !isNative ? ['google'] : []}
            formFields={formFields}
            components={GOOGLE_SSO_UI_ENABLED && isNative ? nativeAuthenticatorComponents : undefined}
          >
            {({ user }) => {
              // On successful authentication, navigate to the app root (calendar)
              if (user) {
                void navigate({ to: '/' });
              }
              return <></>;
            }}
          </Authenticator>

          <p className="mt-6 text-xs text-slate-400">
            Built by{' '}
            <a
              href="https://silvadadallage.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-slate-600"
            >
              Silva Dadallage LLC
            </a>
          </p>
        </div>
      </main>
    </ThemeProvider>
  );
}

export const Route = createFileRoute('/login')({
  // If already authenticated WITH A USABLE SESSION, skip the login page and go to
  // the app root. We use ensureValidSession() (not getCurrentUser()) so that a stale
  // session for a deleted user does NOT auto-redirect into a broken calendar —
  // instead its stale tokens are cleared here and the sign-in page is shown.
  beforeLoad: async () => {
    if (await ensureValidSession()) {
      throw redirect({ to: '/' });
    }
    // No usable session — fall through and render the Authenticator.
  },
  component: AuthPage,
});
